import { useMemo } from 'react';
import { create } from 'zustand';
import { apiService, type ProjectGroupDTO, type ProjectGroupsEntryDTO } from '@/services/api.service';
import { GAMES, supportsGameGroups } from '@/config/games';
import { useGameStore } from '@/stores/game.store';
import { useAuthStore } from '@/stores/auth.store';
import {
  allowedGameGroupsOf,
  describeGameGroup,
  gameGroupHeaderFor,
  groupCapabilities,
  memberGroups,
  ranksOnlyGroups,
  usableGroups,
  type GameGroupLabel,
} from '@/utils/gameGroups';

/**
 * Groupes de jeu de chaque projet (NationsGlory : Nova, Iris...), lus dans
 * `GET /api/panel/project-groups`. Non persisté, comme les pages par projet : une cible ajoutée par
 * un admin doit apparaître au rechargement suivant. Ce qui doit survivre au rechargement (groupe
 * choisi, « ce jeu a des groupes ») vit dans game.store.
 *
 * N'est chargé QUE pour un jeu à groupes (supportsGameGroups) : Paladium ne fait jamais cet appel.
 */
export type GameGroupsStatus = 'idle' | 'loading' | 'loaded' | 'error';

interface GameGroupsState {
  /** { projectId: entrée } ; un projet sans groupes est absent. */
  entries: Record<string, ProjectGroupsEntryDTO>;
  status: GameGroupsStatus;
  /** Motif du dernier échec, null après un succès. */
  error: string | null;
  /** Date du dernier chargement réussi (ms), 0 si jamais. */
  fetchedAt: number;
  /**
   * Charge les groupes. Sans `force`, ne refait pas l'appel si un chargement réussi date de moins de
   * `maxAgeMs` (défaut : jamais relu). Un échec garde les dernières entrées connues.
   */
  fetch: (opts?: { force?: boolean; maxAgeMs?: number }) => Promise<void>;
}

let inFlight: Promise<void> | null = null;

export const useGameGroupsStore = create<GameGroupsState>((set, get) => ({
  entries: {},
  status: 'idle',
  error: null,
  fetchedAt: 0,

  fetch: async (opts) => {
    const { status, fetchedAt } = get();
    if (!opts?.force && status === 'loaded') {
      const maxAge = opts?.maxAgeMs ?? Number.POSITIVE_INFINITY;
      if (Date.now() - fetchedAt < maxAge) return;
    }
    if (inFlight) return inFlight;

    inFlight = (async () => {
      if (get().status !== 'loaded') set({ status: 'loading' });
      const res = await apiService.getProjectGroups();
      if (res.data && typeof res.data === 'object' && !Array.isArray(res.data)) {
        const entries = res.data;
        set({ entries, status: 'loaded', error: null, fetchedAt: Date.now() });
        // « Ce jeu a des groupes » : retenu pour le chargement suivant (attente et repli L9).
        const game = useGameStore.getState();
        for (const g of GAMES) {
          if (supportsGameGroups(g.id)) game.setGameGroupsKnown(g.id, !!entries[g.id]);
        }
      } else {
        set({ status: get().fetchedAt > 0 ? 'loaded' : 'error', error: res.error ?? 'Réponse illisible' });
      }
    })().finally(() => { inFlight = null; });
    return inFlight;
  },
}));

/**
 * Groupe réellement envoyé pour le jeu actif (libellé et couleur), ou null : jeu sans groupes
 * (Paladium), groupes jamais chargés, ou aucun groupe choisi. Sert aux badges et aux confirmations,
 * qui nomment ainsi exactement le groupe qui recevra l'écriture.
 */
export function useActiveGameGroup(): GameGroupLabel | null {
  const gameId = useGameStore((s) => s.activeGameId);
  const key = useGameStore((s) => gameGroupHeaderFor(s.activeGameId, s.gameGroupsKnown, s.activeGameGroupByGame));
  const entry = useGameGroupsStore((s) => s.entries[gameId]);
  return useMemo(() => (key ? describeGameGroup(entry, key) : null), [key, entry]);
}

/**
 * Capacités du satellite du groupe réellement envoyé pour le jeu actif, ou null si elles sont
 * inconnues (jeu sans groupes comme Paladium, groupes pas encore lus, satellite jamais sondé) : dans
 * ce cas rien n'est masqué. Référence stable : celle de l'entrée.
 */
export function useActiveGroupCapabilities(): string[] | null {
  const gameId = useGameStore((s) => s.activeGameId);
  const key = useGameStore((s) => gameGroupHeaderFor(s.activeGameId, s.gameGroupsKnown, s.activeGameGroupByGame));
  const entry = useGameGroupsStore((s) => s.entries[gameId]);
  return useMemo(() => groupCapabilities(entry, key), [entry, key]);
}

/**
 * Groupes « grades seuls » (ranks sans players) de l'affectation, hors groupe actif : la fiche
 * joueur leur donne un cadre de grades chacun. Vide pour un jeu sans groupes.
 */
export function useRanksOnlyGameGroups(gameId: string): ProjectGroupDTO[] {
  const entry = useGameGroupsStore((s) => s.entries[gameId]);
  const allowed = useAllowedGameGroups(gameId);
  const active = useGameStore((s) => gameGroupHeaderFor(gameId, s.gameGroupsKnown, s.activeGameGroupByGame));
  return useMemo(
    () => (supportsGameGroups(gameId) ? ranksOnlyGroups(entry, allowed, active) : []),
    [gameId, entry, allowed, active],
  );
}

/**
 * Groupes de l'affectation du membre du staff sur ce jeu (session /api/auth/me), null = tous.
 * Référence stable : celle de la session.
 */
export function useAllowedGameGroups(gameId: string): string[] | null {
  return useAuthStore((s) => allowedGameGroupsOf(s.user, gameId));
}

/** Groupes du jeu à montrer au membre du staff (son affectation), dans l'ordre du CICD. */
export function useMemberGameGroups(gameId: string): ProjectGroupDTO[] {
  const entry = useGameGroupsStore((s) => s.entries[gameId]);
  const allowed = useAllowedGameGroups(gameId);
  return useMemo(() => memberGroups(entry, allowed), [entry, allowed]);
}

/**
 * Groupes du jeu utilisables pour une requête à groupe imposé (« Créer aussi sur », union des
 * grades protégés) : dans l'affectation du membre du staff ET acceptés par la passerelle.
 */
export function useUsableGameGroups(gameId: string): ProjectGroupDTO[] {
  const entry = useGameGroupsStore((s) => s.entries[gameId]);
  const allowed = useAllowedGameGroups(gameId);
  return useMemo(() => usableGroups(entry, allowed), [entry, allowed]);
}
